import { useState, type ReactNode } from "react";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { styled } from "@mui/material/styles";
import { SCORECARD, type ScorecardKey } from "@shared/scorecard";
import { AnnotatedFieldsSummary } from "./AnnotatedFieldsSummary";
import { AdmissionTracePanel } from "./AdmissionTracePanel";
import { fieldStatus } from "../../../../lib/scorecardFieldStatus";
import type { AnnotationMap, ExtractedScorecardWithEvidence } from "../../../../lib/corpusApi";

const ALL_FIELDS = Object.keys(SCORECARD) as ScorecardKey[];

export interface FieldReviewTabsProps {
  /** The active-field work item (mono or multi panel), shown on the "Review" tab. */
  review: ReactNode;
  scorecard: ExtractedScorecardWithEvidence | null;
  annotation: AnnotationMap | null;
  activeField: ScorecardKey;
  /** corpus source id — used to fetch admission gate data for the "Admission" tab. */
  sourceId: string;
}

const FullHeightStack = styled(Stack)({ height: "100%", minHeight: 0 });

const BorderedTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: 44,
}));

const SmallTab = styled(Tab)({ minHeight: 44, paddingTop: 4, paddingBottom: 4 });

const CountLabel = styled("span")<{ $done: boolean }>(({ theme, $done }) => ({
  fontWeight: 600,
  color: $done ? theme.palette.success.main : theme.palette.text.secondary,
}));

const TabPanel = styled("div")<{ $visible: boolean }>(({ $visible }) => ({
  flex: 1,
  minHeight: 0,
  display: $visible ? "flex" : "none",
  flexDirection: "column",
}));

const ScrollableTabPanel = styled("div")<{ $visible: boolean }>(({ theme, $visible }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(2),
  display: $visible ? "block" : "none",
}));

/**
 * Right-panel container splitting the annotation workspace into three tabs: the active field's
 * work item ("Review"), the whole-document field rollup ("All fields"), and the admission gate
 * result ("Admission"). All panels stay mounted to preserve scroll and override state across
 * tab switches.
 */
export function FieldReviewTabs({
  review,
  scorecard,
  annotation,
  activeField,
  sourceId,
}: FieldReviewTabsProps) {
  const [tab, setTab] = useState(0);

  const resolved = ALL_FIELDS.filter(
    (key) => fieldStatus(key, scorecard, annotation) !== "needs_review"
  ).length;

  return (
    <FullHeightStack>
      <BorderedTabs value={tab} onChange={(_, value: number) => setTab(value)} variant="fullWidth">
        <SmallTab label="Review" />
        <SmallTab
          label={
            <Stack direction="row" gap={0.75} alignItems="center">
              All fields
              <CountLabel $done={resolved === ALL_FIELDS.length}>
                {resolved}/{ALL_FIELDS.length}
              </CountLabel>
            </Stack>
          }
        />
        <SmallTab label="Admission" />
      </BorderedTabs>

      <TabPanel $visible={tab === 0}>{review}</TabPanel>
      <ScrollableTabPanel $visible={tab === 1}>
        <AnnotatedFieldsSummary
          scorecard={scorecard}
          annotation={annotation}
          activeField={activeField}
        />
      </ScrollableTabPanel>
      <ScrollableTabPanel $visible={tab === 2}>
        <AdmissionTracePanel sourceId={sourceId} />
      </ScrollableTabPanel>
    </FullHeightStack>
  );
}
